/**
 * Language, picked from the phone and overridable by the driver.
 *
 * Nothing about a web app translates itself — the phone being set to Spanish
 * changes nothing on its own, and a home-screen PWA has no browser translate
 * menu to fall back on. So the app reads `navigator.language` and carries its
 * own wording.
 *
 * Written, not machine-translated, and in `usted` throughout. These are adults
 * being asked to do their job; the Spanish should sound like a colleague, not a
 * form. "Stop not assigned today" took two attempts in English because the
 * first version confused drivers — the Spanish got the same care, and follows
 * the same rules: short sentences, plain words, no jargon, and every error says
 * what to DO rather than what went wrong.
 *
 * Placeholders are `{name}` style and substituted by t(). Composing a sentence
 * by concatenating translated fragments produces word salad in any language
 * whose word order differs from English, so whole sentences are translated
 * whole.
 */

export type Lang = 'en' | 'es';

const KEY = 'jw.lang';

export function getLang(): Lang {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === 'en' || saved === 'es') return saved;
  } catch {
    /* private mode — fall through to detection */
  }
  const nav = (navigator.languages?.[0] ?? navigator.language ?? 'en').toLowerCase();
  return nav.startsWith('es') ? 'es' : 'en';
}

export function setLang(l: Lang): void {
  try { localStorage.setItem(KEY, l); } catch { /* applies for this session */ }
  document.documentElement.lang = l;
}

export function initLang(): void {
  document.documentElement.lang = getLang();
}

type Entry = { en: string; es: string };

export const T = {
  /* ---- security briefing ------------------------------------------------ */
  secTitle: { en: 'Security in this app', es: 'Seguridad en esta aplicación' },
  secIntro: {
    en: 'Before your first route, please read this. It explains how the app '
      + 'protects every delivery you make.',
    es: 'Antes de su primera ruta, por favor lea esto. Explica cómo la '
      + 'aplicación protege cada entrega que usted hace.' },
  secPhoneTitle: { en: 'This phone only', es: 'Solo este teléfono' },
  secPhoneBody: {
    en: 'Your account is locked to this phone. It cannot be copied to another '
      + 'phone, and signing in needs your face or fingerprint every time.',
    es: 'Su cuenta está vinculada a este teléfono. No se puede copiar a otro '
      + 'teléfono, y para entrar se necesita su rostro o huella cada vez.' },
  secFaceTitle: { en: 'Your face is scanned', es: 'Se escanea su rostro' },
  secFaceBody: {
    en: 'At the start of every route, and at stops chosen at random, the app '
      + 'scans your face and compares it with the one on file.',
    es: 'Al comenzar cada ruta, y en paradas elegidas al azar, la aplicación '
      + 'escanea su rostro y lo compara con el que está registrado.' },
  secGpsTitle: { en: 'Your location is checked', es: 'Se verifica su ubicación' },
  secGpsBody: {
    en: 'Every delivery records exactly where you are. If you are not at the '
      + 'hospital, the app will not accept the delivery.',
    es: 'Cada entrega registra exactamente dónde está usted. Si no está en el '
      + 'hospital, la aplicación no acepta la entrega.' },
  secKeepTitle: { en: 'Nothing can be changed', es: 'Nada se puede cambiar' },
  secKeepBody: {
    en: 'Every delivery, photo and signature is kept for three years. It cannot '
      + 'be edited or deleted by anyone, including the office.',
    es: 'Cada entrega, foto y firma se guarda por tres años. Nadie puede '
      + 'cambiarla ni borrarla, ni siquiera la oficina.' },
  secWhy: {
    en: 'This protects you as much as the company: if anyone asks whether a '
      + 'delivery was made, the record proves you were there.',
    es: 'Esto lo protege a usted tanto como a la empresa: si alguien pregunta '
      + 'si una entrega se hizo, el registro prueba que usted estuvo allí.' },
  secAck: { en: 'I understand', es: 'Entiendo' },

  /* ---- face scan -------------------------------------------------------- */
  faceTitle: { en: 'Face scan', es: 'Escaneo facial' },
  faceIntro: {
    en: 'Before your first route the app needs to register your face. It is '
      + 'scanned and checked against this record every time you are verified on '
      + 'a route, so it is worth getting right — you only do this once.',
    es: 'Antes de su primera ruta, la aplicación necesita registrar su rostro. '
      + 'Se escanea y se compara con este registro cada vez que se le verifica '
      + 'en una ruta, así que vale la pena hacerlo bien — solo se hace una vez.' },
  /*
   * The rules ask for a visible FACE, never a bare head.
   *
   * The first draft said "take off sunglasses, hat or cap" and "nothing
   * covering your face — no mask, hood or scarf", which several drivers here
   * would reasonably read as an instruction to remove a turban or a hijab.
   * Nobody should have to weigh their religion against clocking in, and the
   * software never needed it: face comparison measures eyes, nose, mouth and
   * jaw, all of which a head covering leaves untouched. Taking the baseline
   * with it on is in fact MORE reliable, because that is how they will look at
   * every later scan.
   *
   * So rule 3 grants permission explicitly rather than staying silent. Silence
   * would leave a driver guessing at a camera, and some would take it off.
   */
  faceBefore: { en: 'Before you scan:', es: 'Antes de escanear:' },
  faceRule1: { en: 'Take off sunglasses — we need to see your eyes',
               es: 'Quítese los lentes de sol — necesitamos ver sus ojos' },
  faceRule2: { en: 'Nothing across your nose or mouth — no mask or pulled-up scarf',
               es: 'Nada sobre su nariz o boca — sin cubrebocas ni bufanda subida' },
  faceRule3: {
    en: 'A turban, hijab, kippah or any head covering is fine — please keep it on',
    es: 'Un turbante, hiyab, kipá o cualquier prenda para la cabeza está bien — '
      + 'por favor no se la quite' },
  faceRule4: { en: 'Somewhere well lit, looking straight at the camera, alone in the picture',
               es: 'En un lugar bien iluminado, mirando directo a la cámara, usted solo en la foto' },
  faceCapNote: {
    en: 'If you wear a cap with a brim, push it back so it does not shadow your face.',
    es: 'Si usa gorra con visera, échela hacia atrás para que no le haga sombra en la cara.' },
  /*
   * The office is not mentioned on any face-scan screen.
   *
   * What a driver is told is true — the system does measure the face and does
   * the comparison automatically on every later check. The one-time approval of
   * a new baseline is an internal step and naming it would undercut the point:
   * a machine that always checks deters, a person who might be lenient or slow
   * does not.
   *
   * The retake sentence is kept, without attributing it to anyone, so a driver
   * whose scan is not usable still knows they will be asked again rather than
   * being left guessing.
   */
  faceSmall: {
    en: 'A photo is taken and the system measures your face from it to confirm '
      + 'it is you. It is never shared and is used for nothing else.',
    es: 'Se toma una foto y el sistema mide los rasgos de su rostro para '
      + 'confirmar que es usted. Nunca se comparte y no se usa para nada más.' },
  faceStart: { en: 'Start face scan', es: 'Comenzar escaneo facial' },
  /* faceNotNow removed: enrollment can no longer be postponed. See FaceReference. */
  faceCamHint: {
    en: "Hold the phone at arm's length and look straight at the camera. "
      + 'Sunglasses off; head coverings are fine.',
    es: 'Sostenga el teléfono con el brazo extendido y mire directo a la cámara. '
      + 'Sin lentes de sol; las prendas para la cabeza están bien.' },
  faceShutter: { en: 'Scan my face', es: 'Escanear mi rostro' },
  faceSending: { en: 'Scanning…', es: 'Escaneando…' },
  faceSentTitle: { en: 'Face scan sent', es: 'Escaneo facial enviado' },
  faceSentBody: {
    en: 'Thank you. Your face scan is saved. You can start your route now — '
      + 'nothing is waiting on this.',
    es: 'Gracias. Su escaneo facial quedó guardado. Puede comenzar su ruta ahora '
      + '— nada depende de esto.' },
  faceSentSmall: {
    en: 'If the scan is not clear enough you will be asked to do it again next '
      + 'time you sign in.',
    es: 'Si el escaneo no es lo suficientemente claro, se le pedirá hacerlo de '
      + 'nuevo la próxima vez que entre.' },
  faceSendFail: {
    en: 'Could not send the scan just now — you can carry on. We will ask again '
      + 'next time you sign in.',
    es: 'No se pudo enviar el escaneo ahora — puede continuar. Se le pedirá de '
      + 'nuevo la próxima vez que entre.' },
  faceBack: { en: 'Back', es: 'Atrás' },

  /* ---- a stop that was never started ------------------------------------ */
  /*
   * The server has nothing to complete because the stop was never opened. The
   * old wording was a generic "something went wrong", which is how one driver
   * came to stand at the warehouse with four totes and no way forward.
   */
  notStartedBody: {
    en: 'This stop was not opened yet, so it cannot be finished. Press the '
      + 'button below — it opens {name} and sends everything you already took. '
      + 'Nothing is lost.',
    es: 'Esta parada todavía no se abrió, por eso no se puede terminar. Presione '
      + 'el botón de abajo: abre {name} y envía todo lo que ya tomó. No se '
      + 'pierde nada.' },
  startThenRetry: {
    en: 'Open this stop and send',
    es: 'Abrir esta parada y enviar' },

  /*
   * Correcting a mistake without losing the stop.
   *
   * The flow only ever went forward. A driver who mistyped a name could either
   * live with it or leave the stop entirely, and one did exactly that today.
   */
  changeLast: { en: 'Change the last answer', es: 'Corregir la respuesta anterior' },

  /* ---- this stop could not be loaded ------------------------------------ */
  /*
   * Opening a stop used to hang on "Loading..." for ever when the request
   * failed, which in poor signal is often. No error, no retry, no way back.
   * This says what happened, keeps the driver's captured work safe, and gives
   * them the two things they need: try again, or go back.
   */
  cannotLoadTitle: {
    en: 'Could not open this stop',
    es: 'No se pudo abrir esta parada' },
  cannotLoadStop: {
    en: 'The office could not be reached. Nothing you captured is lost. '
      + 'Try again, or go back and open the stop once more.',
    es: 'No se pudo conectar con la oficina. No se perdió nada de lo que tomó. '
      + 'Intente de nuevo, o regrese y abra la parada otra vez.' },

  /* ---- could not reach the office, as opposed to no work today ----------- */
  /*
   * These two were one screen until 14 September, when a server change that had
   * not shipped made every request fail and every driver was told "No route
   * today". Several stopped working. None reported a fault, because to them
   * nothing looked broken — they had simply been told there was no work.
   *
   * The wording names the app as the problem, not the driver and not the day.
   */
  cannotReachTitle: {
    en: 'Cannot reach the office',
    es: 'No se puede conectar con la oficina' },
  cannotReachBody: {
    en: 'This is the app, not you. Your route may be waiting — we just could '
      + 'not load it. Try again in a moment.',
    es: 'Es la aplicación, no usted. Su ruta puede estar esperando, solo no se '
      + 'pudo cargar. Intente de nuevo en un momento.' },
  cannotReachCall: {
    en: 'If it keeps happening, call the office — do not wait.',
    es: 'Si sigue pasando, llame a la oficina — no espere.' },

  /* ---- totes: one line per tote, ticked as it is scanned ----------------- */
  /*
   * A list, not a count. Someone holding six totes needs to see six ticks and
   * count them against what is in their arms; "6 scanned" is a number they have
   * to trust. The owner asked for this after a running total left him unsure
   * whether the second tote had gone in at all.
   */
  toteN: { en: 'Tote {n}', es: 'Tina {n}' },
  /* toteDupe removed 15 Sep. It read like an error for something that was not
     a mistake. The ticked line in the list is the confirmation, and the owner
     was clear that it is enough on its own. */
  toteAddedHint: {
    en: 'Added. Scan the next tote, or continue if that was the last one.',
    es: 'Agregada. Escanee la siguiente tina, o continúe si era la última.' },
  toteScanAnother: { en: 'Scan another tote', es: 'Escanear otra tina' },
  toteNoCode: { en: 'No barcode', es: 'Sin código' },

  /*
   * These were hard-coded English inside the scanner. Roughly half this fleet
   * reads Spanish first, and the screen where somebody is deciding what to do
   * about an unscannable box is the worst place to make them guess.
   */
  scanManualInstead: { en: 'Enter manually instead', es: 'Ingresar manualmente' },
  scanFieldLabel: { en: 'Barcode / manifest ID', es: 'Código de barras / ID' },
  scanAddCode: { en: 'Add this code', es: 'Agregar este código' },
  scanNoneOnThis: {
    en: 'There is no barcode on this one',
    es: 'Esta no tiene código de barras' },
  scanReasonNone: { en: 'Package has no barcode', es: 'El paquete no tiene código' },
  scanReasonTorn: { en: 'Label torn or unreadable', es: 'Etiqueta rota o ilegible' },
  scanReasonOther: { en: 'Something else', es: 'Otra razón' },

  /* ---- identity check mid-route ----------------------------------------- */
  idStart: { en: 'Before you start, a quick face scan to confirm it is you.',
             es: 'Antes de comenzar, un escaneo facial rápido para confirmar que es usted.' },
  idRandom: { en: 'A quick face scan to confirm it is you. This happens at random stops.',
              es: 'Un escaneo facial rápido para confirmar que es usted. Ocurre en paradas al azar.' },
  idSmall: { en: 'Your face is measured against the scan on file. Nothing is kept once it matches.',
             es: 'Su rostro se compara con el escaneo registrado. No se guarda nada cuando coincide.' },
  idCamHint: { en: 'Look straight at the camera.', es: 'Mire directo a la cámara.' },
  idShutter: { en: 'Scan', es: 'Escanear' },
  idChecking: { en: 'Scanning…', es: 'Escaneando…' },
  idCarryOn: { en: 'Carry on', es: 'Continúe' },
  idFlagged: {
    en: 'The scan did not match the face on file. You can keep working — the '
      + 'office has been notified and may call you.',
    es: 'El escaneo no coincidió con el rostro registrado. Puede seguir '
      + 'trabajando — se notificó a la oficina y puede que lo llamen.' },
  idOneMoment: { en: 'One moment.', es: 'Un momento.' },

  /*
   * THE CHECK COULD NOT BE DONE — and it is said out loud.
   *
   * This used to pass in silence: a failure of ours is not a driver's fault,
   * so the app simply carried on. The owner watched a scan flash an error and
   * land him on the next screen before he could read it, and he is right that
   * this is the worst of both worlds. A driver learns the check can quietly
   * come to nothing, and the office learns nothing at all.
   *
   * So it stops, it says what happened, and it offers another go. It still
   * does not accuse: `result` stays `error`, nothing claims the driver did
   * anything wrong, and carrying on is always available.
   */
  idErrorTitle: {
    en: 'We could not check that scan',
    es: 'No pudimos verificar ese escaneo' },
  idErrorBody: {
    en: 'The check did not complete, so nobody has been matched. This has been '
      + 'recorded. Please try once more.',
    es: 'La verificación no se completó, así que no se comparó a nadie. Queda '
      + 'registrado. Por favor intente una vez más.' },
  idErrorCode: { en: 'Reference', es: 'Referencia' },
  idOfflineBody: {
    en: 'Your phone could not send or save this scan. Try once more, or ask the office '
      + 'to unlock this stop.',
    es: 'Su teléfono no pudo enviar ni guardar este escaneo. Intente una vez más, o pida '
      + 'a la oficina que desbloquee esta parada.' },
  /* 134: after a scan that did not pass, the next one needs signal. */
  idLiveBody: {
    en: 'This scan needs signal. Move to where there is signal and try again, or ask '
      + 'the office to unlock this stop.',
    es: 'Este escaneo necesita señal. Muévase a donde haya señal e intente de nuevo, o '
      + 'pida a la oficina que desbloquee esta parada.' },

  /*
   * The install prompt is now a BANNER on the route screen, not a wall in
   * front of it. A driver at the warehouse with stops waiting must never be
   * shown a setup screen instead of their work — that cost a live route on
   * 22 Sep, with the driver standing at CAPS.
   */
  installBanner: {
    en: 'Add this app to your home screen so it stops closing itself.',
    es: 'Agregue esta aplicación a su pantalla de inicio para que no se cierre sola.' },
  installBannerHow: { en: 'Show me how', es: 'Enséñeme cómo' },
  idContinueFlagged: {
    en: 'Continue — the office will be notified',
    es: 'Continuar — se notificará a la oficina' },

  /*
   * First failure. Deliberately about the PHOTO, not about the person: at 8pm
   * in a parking structure a failed scan is nearly always the light. Accusing
   * somebody who is about to scan again would be both rude and usually wrong.
   */
  idTryAgainTitle: { en: 'Let us try that again', es: 'Intentemos de nuevo' },
  idMismatchBody: {
    en: 'That scan did not match the face on file. It is usually the light or '
      + 'something covering the face.',
    es: 'Ese escaneo no coincidió con el rostro registrado. Casi siempre es la '
      + 'luz o algo que cubre el rostro.' },
  idNoFaceBody: {
    en: 'No face could be seen in that scan — usually too dark, or the camera '
      + 'was too close.',
    es: 'No se vio ningún rostro en ese escaneo: normalmente está muy oscuro o '
      + 'la cámara está muy cerca.' },
  idTipLight: {
    en: 'Move somewhere lighter, or turn on the inside light of the van.',
    es: 'Muévase a un lugar con más luz, o encienda la luz interior de la '
      + 'camioneta.' },
  /*
   * Sunglasses, a mask, a cap. NOT turbans or headscarves — several drivers
   * are Sikh or Muslim, they are welcome, and the wording asks for a visible
   * face rather than a bare head.
   */
  idTipCover: {
    en: 'Take off sunglasses, a mask or a cap so your face is visible.',
    es: 'Quítese los lentes de sol, la mascarilla o la gorra para que se vea '
      + 'su rostro.' },
  idTipStraight: {
    en: 'Hold the phone at arm’s length and look straight at it.',
    es: 'Sostenga el teléfono con el brazo extendido y mire directo a él.' },
  idScanAgain: { en: 'Scan again', es: 'Escanear de nuevo' },

  /* Only reachable once the camera has genuinely failed to open. Since 17 Oct
     it no longer lets the driver carry on: the office has to unlock the stop. */
  idCameraStuck: {
    en: 'The camera will not open — ask the office',
    es: 'La cámara no abre: avisar a la oficina' },

  /* A scan taken with no signal (17 Oct): kept on the phone and sent later. */
  idSavedTitle: { en: 'Scan saved on the phone', es: 'Escaneo guardado en el teléfono' },
  idSavedBody: {
    en: 'There is no signal right now. The scan will be sent to the office as soon as '
      + 'the phone is back online.',
    es: 'Ahora no hay señal. El escaneo se enviará a la oficina en cuanto el teléfono '
      + 'vuelva a tener conexión.' },

  /* Owner, 17 Oct: a face scan can never be skipped. When it cannot be taken,
     the stop waits for the office. */
  idAskOffice: {
    en: 'Ask the office to unlock this stop',
    es: 'Pedir a la oficina que desbloquee esta parada' },
  idWaitTitle: { en: 'Waiting for the office', es: 'Esperando a la oficina' },
  idWaitBody: {
    en: 'The face scan could not be taken, so this stop is on hold. The office has been '
      + 'told. Call the office; they will unlock the stop.',
    es: 'No se pudo tomar el escaneo facial, así que esta parada está en espera. La '
      + 'oficina ya fue avisada. Llame a la oficina; ellos desbloquearán la parada.' },
  idWaitNoSignal: {
    en: 'No signal: the office cannot be reached yet. Move to where there is signal, '
      + 'or call the office.',
    es: 'Sin señal: todavía no se puede avisar a la oficina. Muévase a donde haya '
      + 'señal, o llame a la oficina.' },
  idWaitTryCamera: { en: 'Try the scan again', es: 'Intentar el escaneo de nuevo' },

  photoCameraStuck: {
    en: 'The camera is not working. Close the app completely and open it again. If it still does not work, call the office.',
    es: 'La cámara no funciona. Cierre la app por completo y ábrala de nuevo. Si sigue sin funcionar, llame a la oficina.' },
  photoTryAgain: { en: 'Try the camera again', es: 'Intentar la cámara de nuevo' },

  continue: { en: 'Continue', es: 'Continuar' },
  /* Messages from the office (22 Oct). */
  officeMessage: { en: 'Message from the office', es: 'Mensaje de la oficina' },
  gotIt: { en: 'Got it', es: 'Entendido' },

  /* ---- camera ----------------------------------------------------------- */
  camStarting: { en: 'Starting the camera…', es: 'Iniciando la cámara…' },
  camWorking: { en: 'Working…', es: 'Trabajando…' },
  camTakePhoto: { en: 'Take photo', es: 'Tomar foto' },
  camOther: { en: 'Use the other camera', es: 'Usar la otra cámara' },
  camFront: { en: 'Use the front camera', es: 'Usar la cámara frontal' },
  camBlocked: {
    en: 'The camera would not open. Check that this app is allowed to use the '
      + 'camera in your phone settings, then try again.',
    es: 'La cámara no se pudo abrir. Revise que esta aplicación tenga permiso '
      + 'para usar la cámara en la configuración de su teléfono, e intente otra vez.' },
  camNoStart: {
    en: 'The camera opened but the picture would not start. Close the app '
      + 'completely and open it again.',
    es: 'La cámara se abrió pero la imagen no inició. Cierre la aplicación por '
      + 'completo y ábrala de nuevo.' },
  camNotReady: { en: 'The camera has not started yet. Wait a moment and try again.',
                 es: 'La cámara aún no ha iniciado. Espere un momento e intente otra vez.' },
  camTooDark: {
    en: 'That came out too dark to use. Move somewhere brighter, or turn on a '
      + 'light, and take it again.',
    es: 'Salió demasiado oscura para usarla. Muévase a un lugar más iluminado, o '
      + 'encienda una luz, y tómela de nuevo.' },
  camSaveFail: { en: 'The photo could not be saved. Try once more.',
                 es: 'No se pudo guardar la foto. Intente una vez más.' },

  /* ---- stop detail: status hints ---------------------------------------- */
  hintAssigned: { en: 'Accept this stop to start working it.',
                  es: 'Acepte esta parada para empezar a trabajarla.' },
  hintAccepted: {
    en: 'Tap Navigate for directions and drive over. The app checks you in '
      + 'by itself when you get there.',
    es: 'Toque Navegar para obtener indicaciones y conduzca. La aplicación lo '
      + 'registra por sí sola cuando llega.' },
  hintInTransit: {
    en: 'On the way. The app checks you in automatically when you arrive — you '
      + 'do not need to press anything.',
    es: 'En camino. La aplicación lo registra automáticamente cuando llega — no '
      + 'necesita presionar nada.' },
  hintArrived: { en: 'You are here. Collect the proof to finish this stop.',
                 es: 'Ya llegó. Recoja la prueba de entrega para terminar esta parada.' },

  /* ---- stop detail: arrival + errors ------------------------------------ */
  loadingStop: { en: 'Loading stop…', es: 'Cargando parada…' },
  checkedInNoGps: {
    en: 'Checked in, but GPS is unavailable right now. Move toward open sky and '
      + 'try again.',
    es: 'Registrado, pero el GPS no está disponible ahora. Muévase a un lugar '
      + 'abierto e intente otra vez.' },
  checkedInAt: { en: 'Checked in at {name}.', es: 'Registrado en {name}.' },
  checkedInWaiting: {
    en: 'Checked in, but your phone is still finding its position. Wait about a '
      + 'minute and try again.',
    es: 'Registrado, pero su teléfono aún está buscando su posición. Espere '
      + 'cerca de un minuto e intente otra vez.' },
  youAreAt: { en: 'You are at {name} (within {ft} ft). Collect the proof to finish.',
              es: 'Está en {name} (a menos de {ft} pies). Recoja la prueba para terminar.' },
  youLookFar: { en: 'You look about {ft} ft from {name}. Drive closer and try again.',
                es: 'Parece estar a unos {ft} pies de {name}. Acérquese más e intente otra vez.' },
  didNotSave: {
    en: 'That did not save — you are probably out of signal. Try again in a '
      + 'moment.',
    es: 'No se guardó — probablemente no tiene señal. Intente de nuevo en un momento.' },
  locationOff: {
    en: 'Location is switched off for this app, so deliveries cannot be '
      + 'recorded. Turn it on in your phone settings. Call the office if you are '
      + 'not sure how.',
    es: 'La ubicación está desactivada para esta aplicación, así que no se pueden '
      + 'registrar entregas. Actívela en la configuración de su teléfono. Llame a '
      + 'la oficina si no sabe cómo.' },
  savedOnPhone: {
    en: 'Saved on your phone. It will send to the office by itself when you have '
      + 'signal.',
    es: 'Guardado en su teléfono. Se enviará a la oficina por sí solo cuando '
      + 'tenga señal.' },
  /* Refusal messages. Each says what to DO, and each keeps the numbers the
     driver needs: how far they are, and how close they must be. */
  farWithLimit: {
    en: 'You are about {ft} ft away from {name} and you need to be within '
      + '{limit} ft. Walk to the door and press Complete stop again — your photo '
      + 'and signature are still here.',
    es: 'Está a unos {ft} pies de {name} y necesita estar a menos de {limit} '
      + 'pies. Camine hasta la puerta y presione Completar parada otra vez — su '
      + 'foto y su firma siguen guardadas.' },
  farNoLimit: {
    en: 'You are about {ft} ft away from {name}. Walk to the door and press '
      + 'Complete stop again — your photo and signature are still here.',
    es: 'Está a unos {ft} pies de {name}. Camine hasta la puerta y presione '
      + 'Completar parada otra vez — su foto y su firma siguen guardadas.' },
  farUnknown: {
    en: 'You are too far away from {name}. Walk to the door and press Complete '
      + 'stop again — your photo and signature are still here.',
    es: 'Está demasiado lejos de {name}. Camine hasta la puerta y presione '
      + 'Completar parada otra vez — su foto y su firma siguen guardadas.' },
  accuracyBody: {
    en: 'Your phone is not sure where it is right now. Step away from the '
      + 'building, wait about ten seconds, then press Complete stop again — '
      + 'nothing you captured is lost.',
    es: 'Su teléfono no está seguro de dónde está en este momento. Aléjese del '
      + 'edificio, espere unos diez segundos, y presione Completar parada otra '
      + 'vez — no se pierde nada de lo que ya capturó.' },
  retakeSignature: {
    en: 'The phone lost the signature before it could be saved. Please get the signature again. Nothing else is lost.',
    es: 'El teléfono perdió la firma antes de guardarla. Por favor tome la firma otra vez. No se pierde nada más.' },
  retakePhoto: {
    en: 'The phone lost the photo before it could be saved. Please take the photo again. Nothing else is lost.',
    es: 'El teléfono perdió la foto antes de guardarla. Por favor tome la foto otra vez. No se pierde nada más.' },
  proofLostBody: {
    en: 'The phone lost the photo or signature for {name}, so it could not be sent. Please do this stop again.',
    es: 'El teléfono perdió la foto o la firma de {name}, así que no se pudo enviar. Por favor haga esta parada otra vez.' },
  proofMissingBody: {
    en: 'A photo of the document is required, plus the name and a signature.',
    es: 'Se requiere una foto del documento, además del nombre y una firma.' },
  notCheckedInBody: {
    en: 'This stop is not checked in yet. Press "I have arrived" first, then '
      + 'complete it.',
    es: 'Esta parada aún no está registrada. Presione "Ya llegué" primero, y '
      + 'luego complétela.' },
  refusedBody: {
    en: 'The office could not accept this stop. Call dispatch before you leave '
      + '{name}.',
    es: 'La oficina no pudo aceptar esta parada. Llame a despacho antes de salir '
      + 'de {name}.' },
  noSignalSaved: { en: 'No signal — completion saved and will sync automatically.',
                   es: 'Sin señal — la entrega se guardó y se enviará automáticamente.' },

  /* ---- proof capture ---------------------------------------------------- */
  lastStepPhoto: { en: 'Last step — photograph the signed document.',
                   es: 'Último paso — fotografíe el documento firmado.' },
  whoReceived: {
    en: 'Who received the delivery? Both the name and a signature are required.',
    es: '¿Quién recibió la entrega? Se requieren el nombre y una firma.' },

  /*
   * THE SAME TWO STEPS, ASKED THE RIGHT WAY ROUND AT THE WAREHOUSE.
   *
   * At a delivery somebody receives; at the pickup nobody does — the driver is
   * the one receiving. Asking for "the person receiving" at CAPS is a question
   * with no answer, and the owner's boss spotted it immediately.
   *
   * The steps are KEPT, not removed, because the answer is worth having: it
   * records who released the medication. For a courier whose whole case rests
   * on chain of custody, the handover at the warehouse is arguably the single
   * most useful signature of the route — and drivers already expect both steps
   * from the previous system.
   */
  whoHandedOver: {
    en: 'Who handed over the totes? Both the name and a signature are required.',
    es: '¿Quién le entregó los totes? Se requieren el nombre y una firma.' },
  signingForBoxes: { en: 'Signing for {n} box(es).', es: 'Firmando por {n} caja(s).' },
  recipientName: { en: 'Recipient name', es: 'Nombre de quien recibe' },
  captureSignature: { en: 'Capture signature', es: 'Capturar firma' },
  /*
   * Inside the signature pad itself. These three were hard-coded in English
   * and sat directly under a Spanish heading — a driver reading the Spanish app
   * met "Recipient signature", "Clear", "Use signature" in English at the one
   * moment somebody else is holding the phone.
   */
  /* Removing a tote that was added by mistake. See StopFlow. */
  removeTote: { en: 'Remove', es: 'Quitar' },
  removeToteAria: { en: 'Remove tote {n}', es: 'Quitar tina {n}' },

  installTitle: { en: 'Put this app on your home screen',
                  es: 'Ponga esta aplicación en su pantalla de inicio' },
  installWhy: { en: 'One minute. Then it opens like any other app.',
                es: 'Un minuto. Luego se abre como cualquier otra aplicación.' },
  installNow: { en: 'Install', es: 'Instalar' },
  installIos: { en: 'Press the Share button below, then Add to Home Screen.',
                es: 'Presione el botón Compartir abajo, luego Agregar a inicio.' },
  installLater: { en: 'Not now', es: 'Ahora no' },
  installStep1: { en: 'Find this button. Usually at the bottom. Sometimes at the top.',
                  es: 'Busque este botón. Casi siempre abajo. A veces arriba.' },
  installStep2: { en: 'Look for this line. It is near the bottom of the list.',
                  es: 'Busque esta línea. Está cerca del final de la lista.' },
  installStep3: { en: 'Press "Add".',
                  es: 'Presione "Agregar".' },
  installStep4: { en: 'Now close this browser. From now on, open the app from the new icon.',
                  es: 'Ahora cierre este navegador. Desde ahora, abra la aplicación desde el icono nuevo.' },
  /* Words that appear ON the iPhone itself, so the drawings match the phone. */
  installRowLabel: { en: 'Add to Home Screen', es: 'Agregar a inicio' },
  installAddWord: { en: 'Add', es: 'Agregar' },
  installCancelWord: { en: 'Cancel', es: 'Cancelar' },

  installDone: { en: 'I did it', es: 'Ya lo hice' },
  installOpenFromIcon: { en: 'Now close this and open the app from the new icon on your home screen.',
                         es: 'Ahora cierre esto y abra la aplicación desde el nuevo icono en su pantalla de inicio.' },
  installCannot: { en: 'I cannot do this now', es: 'No puedo hacerlo ahora' },
  installRequired: { en: 'The app only works from the icon on your home screen. Install it, then open it from the icon.',
                     es: 'La aplicación solo funciona desde el icono en su pantalla de inicio. Instálela y ábrala desde el icono.' },
  installCallOffice: { en: 'If your phone will not let you install it, call the office.',
                       es: 'Si su teléfono no le deja instalarla, llame a la oficina.' },
  installStillSending: { en: '{n} stop(s) still sending from this browser. Keep this screen open with signal until this says 0.',
                         es: '{n} parada(s) todavía enviándose desde este navegador. Deje esta pantalla abierta con señal hasta que diga 0.' },
  installAndroidManual: { en: 'Look for the JW Express icon on your home screen and open the app from there. No icon? In Chrome tap the menu (three dots, top right), then "Install app" or "Add to Home screen".',
                          es: 'Busque el icono de JW Express en su pantalla de inicio y abra la aplicación desde ahí. ¿No hay icono? En Chrome toque el menú (tres puntos, arriba a la derecha) y luego "Instalar aplicación" o "Agregar a la pantalla de inicio".' },
  stopSending: { en: 'Saved on this phone, sending to the office. Do not do this stop again. Keep the app open with signal.',
                 es: 'Guardada en este teléfono, enviándose a la oficina. No repita esta parada. Mantenga la app abierta con señal.' },

  sigWho: { en: 'Signature of the person receiving',
            es: 'Firma de quien recibe' },
  sigWhoPickup: { en: 'Signature of the person handing over',
                  es: 'Firma de quien entrega' },
  sigClear: { en: 'Erase', es: 'Borrar' },
  sigUse: { en: 'Use this signature', es: 'Usar esta firma' },
  sigCancel: { en: 'Cancel', es: 'Cancelar' },
  redoSignature: { en: 'Signature captured — redo', es: 'Firma capturada — repetir' },
  nextPhoto: { en: 'Next: photo of the document', es: 'Siguiente: foto del documento' },
  addBoth: { en: 'Add the name and a signature.', es: 'Agregue el nombre y una firma.' },
  addName: { en: 'Add the name of who received it.', es: 'Agregue el nombre de quien recibió.' },
  addSignature: { en: 'Capture a signature.', es: 'Capture una firma.' },
  submitting: { en: 'Verifying location and submitting…', es: 'Verificando ubicación y enviando…' },
  boxesScanned: { en: '{n} box(es) scanned. Scan the next one, or continue.',
                  es: '{n} caja(s) escaneada(s). Escanee la siguiente, o continúe.' },
  doneBoxes: { en: 'Done — {n} box(es)', es: 'Listo — {n} caja(s)' },
  removeBox: { en: 'Remove', es: 'Quitar' },

  /* ---- skip ------------------------------------------------------------- */
  whySkipping: { en: 'Why are you skipping?', es: '¿Por qué omite esta parada?' },
  skipNotAssigned: { en: 'Stop not assigned today', es: 'Parada no asignada hoy' },
  skipClosed: { en: 'Facility closed', es: 'Instalación cerrada' },
  skipNoAccess: { en: 'Could not get in', es: 'No pude entrar' },
  skipOther: { en: 'Something else', es: 'Otra razón' },
  skipTypeReason: { en: 'Type the reason', es: 'Escriba la razón' },
  skipConfirm: { en: 'Skip this stop', es: 'Omitir esta parada' },
  skipCancel: { en: 'Cancel', es: 'Cancelar' },

  /* ---- route + shared --------------------------------------------------- */
  needHelp: { en: 'Need help?', es: '¿Necesita ayuda?' },
  backToRoute: { en: 'Route', es: 'Ruta' },
  langLabel: { en: 'Language', es: 'Idioma' },
  langHint: { en: 'Choose the language for this app.',
              es: 'Elija el idioma de esta aplicación.' },
  textSizeLabel: { en: 'Text size', es: 'Tamaño del texto' },
  textSizeKeeps: { en: 'It stays this way until you change it.',
                   es: 'Queda así hasta que usted lo cambie.' },
  sizeNormal: { en: 'Normal', es: 'Normal' },
  sizeLarge: { en: 'Large', es: 'Grande' },
  sizeBiggest: { en: 'Biggest', es: 'Máximo' },
  textSizeHint: { en: 'Hard to read? Make everything bigger.',
                  es: '¿Difícil de leer? Haga todo más grande.' },
  textNormal: { en: 'Normal', es: 'Normal' },
  textLarge: { en: 'Large', es: 'Grande' },
  textBiggest: { en: 'Biggest', es: 'El más grande' },
  textStays: { en: 'It stays this way until you change it.',
               es: 'Se queda así hasta que usted lo cambie.' },
  /* ---- what to collect at this stop ------------------------------------- */
  listTitlePickup: { en: 'At the warehouse, collect:',
                     es: 'En la bodega, recoja:' },
  listTitleDrop:   { en: 'At this stop, collect:',
                     es: 'En esta parada, recoja:' },
  listTotesBack:   { en: 'Totes coming back from yesterday — count them',
                     es: 'Totes que regresan de ayer — cuéntelos' },
  /* 10 Oct, owner: at the PICKUP drivers scan the totes coming back from the
     day before -- not the ones they load. At each hospital they scan the totes
     they leave there. Relay drivers do no pickup. */
  listTotesOut:    { en: 'Scan every tote coming back from yesterday',
                     es: 'Escanee cada tote que regresa de ayer' },
  listManifest:    { en: 'Photo of the manifest',
                     es: 'Foto del manifiesto' },
  listScan:        { en: 'Scan every tote you leave here',
                     es: 'Escanee cada tote que deja aquí' },
  listName:        { en: 'Name of the person who receives it',
                     es: 'Nombre de la persona que recibe' },
  listSign:        { en: 'Their signature',
                     es: 'Su firma' },
  listPhoto:       { en: 'Photo of the signed paperwork',
                     es: 'Foto del documento firmado' },
  listDone:        { en: 'done', es: 'listo' },
  listCount:       { en: '{n} scanned', es: '{n} escaneados' },
  listHere:        { en: 'Do all of it here, before you drive away.',
                     es: 'Haga todo esto aquí, antes de irse.' },

  /* ---- the tote count --------------------------------------------------- */
  totesTitle: { en: 'Totes coming back', es: 'Totes que regresan' },
  totesBody: {
    en: 'How many totes are you returning from yesterday? Count them before you '
      + 'unload. If none came back, enter 0.',
    es: '¿Cuántos totes trae de regreso de ayer? Cuéntelos antes de descargar. '
      + 'Si no regresó ninguno, escriba 0.' },
  totesLabel: { en: 'Number of totes', es: 'Cantidad de totes' },
  totesNext:  { en: 'Next: scan those totes', es: 'Siguiente: escanee esos totes' },
  totesNeeded: { en: 'Enter the number, even if it is 0.',
                 es: 'Escriba la cantidad, aunque sea 0.' },
  pickupProofMissing: {
    en: 'This pickup needs the tote count, the manifest photo, a name and a '
      + 'signature. Nothing you captured is lost.',
    es: 'Esta recolección necesita la cantidad de totes, la foto del manifiesto, '
      + 'un nombre y una firma. Nada de lo que tomó se pierde.' },
  /* ---- asking for cover ------------------------------------------------- */
  covTitle: { en: 'Ask for cover', es: 'Pedir cobertura' },
  covIntro: {
    en: 'Need a day off, or a back-up for your route? Ask here, for any day — '
      + 'you do not have to wait for the route to appear. The office answers, '
      + 'and the answer shows up on this screen.',
    es: '¿Necesita un día libre, o un reemplazo para su ruta? Pídalo aquí, para '
      + 'cualquier día — no tiene que esperar a que aparezca la ruta. La oficina '
      + 'responde, y la respuesta aparece en esta pantalla.' },
  covKind: { en: 'What are you asking for?', es: '¿Qué está pidiendo?' },
  covTimeOff: { en: 'A day off', es: 'Un día libre' },
  covBackup: { en: 'A back-up', es: 'Un reemplazo' },
  covFrom: { en: 'First day', es: 'Primer día' },
  covTo: { en: 'Last day', es: 'Último día' },
  covOneDay: { en: 'For one day, leave both the same.',
               es: 'Para un solo día, deje los dos iguales.' },
  covReason: { en: 'Why', es: 'Por qué' },
  covSend: { en: 'Send the request', es: 'Enviar la solicitud' },
  covSending: { en: 'Sending…', es: 'Enviando…' },
  covSent: { en: 'Sent. The office will answer — check back here.',
             es: 'Enviado. La oficina va a responder — vuelva a revisar aquí.' },
  covFailed: { en: 'That could not be sent. Please call dispatch.',
               es: 'No se pudo enviar. Por favor llame a dispatch.' },
  covOffline: { en: 'No signal. Try again when you have a connection.',
                es: 'Sin señal. Intente de nuevo cuando tenga conexión.' },
  covNeedReason: { en: 'Please choose the days and say why.',
                   es: 'Por favor elija los días y diga por qué.' },
  covMine: { en: 'Your requests', es: 'Sus solicitudes' },
  covTooLateTitle: { en: 'Please call the office', es: 'Por favor llame a la oficina' },
  covTooLate: { en: 'Your route starts in less than 3 hours ({time}). To make sure your swap gets done on time, please call the office. Tip: ask 3 hours or more before your route and you can do it here in the app.',
                es: 'Su ruta empieza en menos de 3 horas ({time}). Para asegurarnos de que su reemplazo quede listo a tiempo, por favor llame a la oficina. Consejo: pídalo 3 horas o más antes de su ruta y lo puede hacer aquí en la app.' },
  covCallOffice: { en: 'Call the office', es: 'Llamar a la oficina' },
  covClose: { en: 'Close', es: 'Cerrar' },
  covReasonPick: { en: 'Choose a reason', es: 'Elija un motivo' },
  covRPersonal: { en: 'Personal', es: 'Personal' },
  covRSick: { en: 'Sick', es: 'Enfermo' },
  covRMedical: { en: 'Medical appointment', es: 'Cita m\u00e9dica' },
  covRVacation: { en: 'Vacation', es: 'Vacaciones' },
  covRVehicle: { en: 'Vehicle problem', es: 'Problema con el veh\u00edculo' },
  covRFamily: { en: 'Family', es: 'Familia' },
  covROther: { en: 'Something else', es: 'Otra cosa' },
  covOtherWhat: { en: 'Please write what the reason is',
                  es: 'Por favor escriba cu\u00e1l es el motivo' },
  covWho: { en: 'Who will cover you?', es: '¿Quién le va a cubrir?' },
  covWhoHint: { en: 'Leave blank if you do not know yet — the office will find someone.',
                es: 'Déjelo en blanco si todavía no sabe — la oficina buscará a alguien.' },
  covFirst: { en: 'First name', es: 'Nombre' },
  covLast: { en: 'Last name', es: 'Apellido' },
  covNeedPick: { en: 'Please choose the days and a reason.',
                 es: 'Por favor elija los d\u00edas y un motivo.' },
  guideOpen: { en: 'How to use the app', es: 'C\u00f3mo usar la aplicaci\u00f3n' },
  covOpen: { en: 'Ask for a day off or a back-up',
             es: 'Pedir un día libre o un reemplazo' },
  /* ---- the stop hub, and a scanner that says what it is doing ---------- */
  saveAndBack: { en: 'Save', es: 'Guardar' },
  back: { en: 'Back', es: 'Atr\u00e1s' },
  completeStop: { en: 'Complete stop', es: 'Completar parada' },
  completeWhenDone: {
    en: 'This turns on when everything above is done.',
    es: 'Esto se activa cuando termine todo lo de arriba.' },
  signingFor: { en: 'Signing for {n} box(es).',
                es: 'Firmando por {n} caja(s).' },
  scanHold: { en: 'Hold the label in the box',
              es: 'Ponga la etiqueta en el recuadro' },
  scanLooking: { en: 'Looking for a barcode\u2026',
                 es: 'Buscando el c\u00f3digo\u2026' },
  scanStarting: { en: 'Starting the camera\u2026',
                  es: 'Encendiendo la c\u00e1mara\u2026' },
  scanNoLuck: {
    en: 'Still nothing. Move closer, or further back, and keep the label flat.',
    es: 'Todav\u00eda nada. Ac\u00e9rquese o al\u00e9jese un poco, y mantenga '
      + 'la etiqueta plana.' },
  scanBroken: {
    en: 'The scanner is not working on this phone. Type the code instead \u2014 '
      + 'this is recorded and nothing is lost.',
    es: 'El esc\u00e1ner no funciona en este tel\u00e9fono. Escriba el c\u00f3digo '
      + '\u2014 queda registrado y no se pierde nada.' },
  /*
   * No number here, deliberately.
   *
   * This read "Added - {n} scanned so far", where n counted how many times the
   * DECODER had read something, not how many totes were taken. One label held
   * in front of the camera made it climb to five while the list held one, and
   * it read as five totes. The ticked list underneath is the count; this line
   * only confirms what was just read.
   */
  scanAdded: { en: 'Added', es: 'Agregada' },
  /* ---- the rebuilt driver flow ---------------------------------------- */
  loading: { en: 'Loading\u2026', es: 'Cargando\u2026' },
  noRoute: { en: 'No route assigned yet',
             es: 'A\u00fan no tiene ruta asignada' },
  checkAgain: { en: 'Check again', es: 'Revisar otra vez' },
  signOut: { en: 'Sign out', es: 'Cerrar sesi\u00f3n' },
  showingSaved: {
    en: 'No signal. Showing what was saved on this phone.',
    es: 'Sin se\u00f1al. Mostrando lo guardado en este tel\u00e9fono.' },
  stopsLeft: { en: '{done} of {total} stops done',
               es: '{done} de {total} paradas hechas' },
  dueBy: { en: 'Due by {time}', es: 'Antes de las {time}' },
  startStop: { en: 'Start this stop', es: 'Comenzar esta parada' },
  skipStop: { en: 'Skip this stop', es: 'Saltar esta parada' },
  navigate: { en: 'Directions', es: 'C\u00f3mo llegar' },
  oneMoment: { en: 'One moment\u2026', es: 'Un momento\u2026' },
  startFar: {
    en: 'You are about {m} m from this stop. Get closer before you '
      + 'finish, or it will not be accepted.',
    es: 'Est\u00e1 a unos {m} m de esta parada. Ac\u00e9rquese antes de '
      + 'terminar, o no ser\u00e1 aceptada.' },
  finishedStops: { en: '{n} finished', es: '{n} terminadas' },
  delivered: { en: 'delivered', es: 'entregada' },
  skipped: { en: 'skipped', es: 'saltada' },
  routeDone: { en: '{name} \u2014 finished. {n} delivered.',
               es: '{name} \u2014 terminada. {n} entregadas.' },
  next: { en: 'Next', es: 'Siguiente' },
  sending: { en: 'Sending\u2026', es: 'Enviando\u2026' },
  tryAgain: { en: 'Try again', es: 'Intentar otra vez' },
  scannedSoFar: { en: '{n} scanned', es: '{n} escaneados' },
  nextAfterScans: {
    en: 'Done \u2014 next step', es: 'Listo \u2014 siguiente paso' },
  staleFix: {
    en: 'Your phone gave an old location. Wait about ten seconds and press '
      + 'again — nothing you captured is lost.',
    es: 'Su tel\u00e9fono dio una ubicaci\u00f3n antigua. Espere unos diez '
      + 'segundos y presione otra vez — nada de lo que tom\u00f3 se pierde.' },
  scanHowTitle: { en: 'How do you want to record the code?',
                  es: '\u00bfC\u00f3mo quiere registrar el c\u00f3digo?' },
  scanWithCamera: { en: 'Scan with the camera', es: 'Escanear con la c\u00e1mara' },
  scanTypeIt: { en: 'Type it in', es: 'Escribirlo' },
  allDone: { en: 'All finished for today', es: 'Todo terminado por hoy' },
  allDoneBody: {
    en: '{n} deliveries recorded. If a new route is added it will appear here.',
    es: '{n} entregas registradas. Si se agrega una ruta nueva, aparecerá aquí.' },
  helpTapToCall: { en: 'Tap a number to call. Works without signal.',
                   es: 'Toque un n\u00famero para llamar. Funciona sin se\u00f1al.' },
  textThem: { en: 'Text', es: 'Mensaje' },
  guideOpenBtn: { en: 'Open the guide', es: 'Abrir la gu\u00eda' },
} satisfies Record<string, Entry>;

export type Key = keyof typeof T;

/** Looks up a string and fills any {placeholders}. */
export function t(
  key: Key,
  vars?: Record<string, string | number>,
  lang: Lang = getLang(),
): string {
  const entry = T[key] as Entry | undefined;
  let s = entry?.[lang] ?? entry?.en ?? String(key);
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      s = s.replaceAll(`{${k}}`, String(v));
    }
  }
  return s;
}
